import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import { trackClientEvent } from 'analytics/analytics'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'

const SectionContainer = styled.div`
  width: 100%;
  padding-top: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(32)};
  }
`

const Content = styled(LayoutContainer)`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
`

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  width: 100%;
  min-height: ${rem(179)};
  padding: ${rem(16)};
  border-radius: ${rem(8)};
  background: linear-gradient(90deg, #304DAB 14.364%, #4C82DF 85.903%);
  color: inherit;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(24)};
    min-height: ${rem(104)};
    padding: ${rem(28)} ${rem(24)};
  }
`

const Logo = styled(LuxPlusLogo)`
  display: block;
  flex: 0 0 auto;
  width: ${rem(96)};
  height: auto;
  color: inherit;

  ${mediaQueryUp.tablet} {
    width: ${rem(125)};
  }
`

const Copy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(16)};
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(20)};
  }
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(4)};
  }
`

const HeadlineText = styled(BodyText)`
  margin: 0;
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  font-size: 18px;
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: 22px;

  ${mediaQueryUp.tablet} {
    font-size: 20px;
    line-height: 24px;
  }
`

const SupportingText = styled(BodyText)`
  margin: 0;
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  font-size: 14px;
  line-height: 20px;
`

const HighlightText = styled.span`
  color: #00E5A5;
`

const CtaButton = styled(TextButton)`
  && {
    min-height: ${rem(48)};
    min-width: ${rem(161)};
    padding-right: ${rem(16)};
    padding-left: ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    && {
      flex: 0 0 auto;
    }
  }
`

function FlightsLuxPlusPreviewPrompt() {
  const intl = useIntl()
  const freePreviewModalInvitePillCopy = intl.formatMessage({
    id: 'O0LSyNorgE7Ww4l05fyW',
    defaultMessage: 'Discounted flights',
    description: 'Pill text shown in the LuxPlus free preview modal launched from the flights landing page',
  })
  const { handleStartFreePreview } = useLuxPlusLandingActions(freePreviewModalInvitePillCopy)

  useEffect(() => {
    trackClientEvent({
      action: 'impression',
      subject: 'free_preview_on_flight_page',
      category: 'lux_plus',
      type: 'nonInteraction',
    })
  }, [])

  return (
    <SectionContainer>
      <Content variant="large">
        <Banner data-testid="lux-plus-flight-preview-banner">
          <Copy>
            <Logo colour="inherit" aria-hidden="true" />
            <TextContent>
              <HeadlineText as="p" variant="medium">
                <FormattedMessage
                  id="yX--xZkAij8DFWEBUFIh"
                  defaultMessage="Unlock <highlight>member only</highlight> flight prices"
                  description="Headline for the non-member LuxPlus flight pricing preview banner on the flights landing page"
                  values={{
                    highlight: (chunks) => <HighlightText>{chunks}</HighlightText>,
                  }}
                />
              </HeadlineText>
              <SupportingText as="p" variant="medium">
                <FormattedMessage
                  id="13Tou_HLkE6LXL8QfS-O"
                  defaultMessage="Start a free preview to compare discount prices across all flights"
                  description="Supporting copy for the non-member LuxPlus flight pricing preview banner on the flights landing page"
                />
              </SupportingText>
            </TextContent>
          </Copy>
          <ProductPaletteProvider palette="lux-plus">
            <CtaButton
              kind="primary"
              variant="ghost"
              size="large"
              fit="mobile-full-width"
              onClick={handleStartFreePreview}
            >
              <FormattedMessage
                id="w1Wdz75_CwQxk5GeO4Ur"
                defaultMessage="Start free preview"
                description="Button text to open the LuxPlus+ free preview modal from the flights landing page"
              />
            </CtaButton>
          </ProductPaletteProvider>
        </Banner>
      </Content>
    </SectionContainer>
  )
}

export default FlightsLuxPlusPreviewPrompt
